import {Injectable, signal} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {Params} from '@angular/router';
import {CommonTableData} from '@lib-ui/common-table/models/common-table-data.class';
import {MessageService} from '@lib-services/message.service';
import {IOrder, IOrderTableData} from '@lib-typing/order.interface';
import {OrdersService} from '@lib-services/orders.service';
import {ICommonTableConfig, ICommonTableService} from '@lib-ui/common-table/models/common-table.interface';
import {IPaginatedResponse} from '@lib-typing/response.interface';
import {PartSearchService} from '@lib-services/part-search.service';
import { setDayRange } from '../../../../../../libs/utils/date-format';
import { TimeZoneService } from '@lib-services/time-zone.service';
import { EnumTranslateService } from '@lib-services/enum-translate.service';

@Injectable({
    providedIn: 'root',
})
export class OrderTableService implements ICommonTableService<IOrderTableData> {
    public tableData$: BehaviorSubject<CommonTableData<IOrderTableData>> =
        new BehaviorSubject<CommonTableData<IOrderTableData>>(new CommonTableData());
    defaultFilterParams = {
        sortBy: 'updatedAt:DESC',
    };
    private readonly commonTableData = new CommonTableData();
    loading = signal<boolean>(false);

    constructor(
        private ordersService: OrdersService,
        private messageService: MessageService,
        private partSearchService: PartSearchService,
        private enumTranslateService: EnumTranslateService,
        private timeZoneService: TimeZoneService
    ) {}

    updateData(config: ICommonTableConfig): void {
        this.loading.set(true);
        this.ordersService
            .getList(this.createSearchParams(config))
            .subscribe({
                next: response => {
                    this.tableData$.next(new CommonTableData(this.transformData(response)));
                },
                error: () => this.messageService.errorMessage('Ошибка загрузки списка задач'),
            })
            .add(() => this.loading.set(false));
    }

    createSearchParams(config: ICommonTableConfig): Params {
        this.commonTableData.data.length = 0;
        const { pageIndex, itemsPerPage } = config.paginatorConfig;
        const { filters } = config;

        let searchParams: Params = {
            ...this.defaultFilterParams,
            limit: itemsPerPage.toString(),
        };

        if (pageIndex) {
            searchParams = {
                ...searchParams,
                page: pageIndex.toString(),
            };
        }

        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value) {
                    let filterValue;
                    let params: string[];
                    switch (key) {
                    case 'addressDetails':
                        params = String(value).split(' ').map((el: string) => el.trim());
                        filterValue = [`$ilike:${params[0]}`].concat(this.partSearchService.createMultiStringParams(params.slice(1)));
                        break;
                    case 'executor.lastName':
                        filterValue = `$ilike:${value}`;
                        break;
                    case 'id':
                        filterValue = `$eq:${value}`;
                        break;
                    case 'dateTo':
                    case 'createdAt':
                        filterValue = setDayRange(value as string);
                        break;
                    default:
                        filterValue = `${value}`;
                    }
                    searchParams = {
                        ...searchParams,
                        [`filter.${key}`]: filterValue,
                    };
                }
            });
        }

        return searchParams;
    }

    transformData(response: IPaginatedResponse<IOrder>): IPaginatedResponse<IOrderTableData> {
        const transformedData = response.data.map(order => {
            return {
                id: order.id,
                createdAt: order.createdAt,
                dateFrom: order.dateFrom,
                executorName: !order.executor ? 'Не назначена' : `${order.executor?.firstName || ''} ${order.executor?.lastName || ''}`,
                workTime: `${this.getWorkTime(order.dateFrom)}-${this.getWorkTime(order.dateTo)}`,
                groupName: order.group?.name,
                projectName: order.project?.name,
                orderStatus: this.enumTranslateService.translateStatus(order.status),
                discount: !!order.discount,
                conditionsCheck: order.conditionsCheck,
                address: order.addressDetails || '',
            } as IOrderTableData;

        });
        return {
            ...response,
            data: transformedData
        };
    }

    // отображение рабочего времени относительно временной зоны
    getWorkTime(date: Date): string {
        const hours = (new Date(date).getUTCHours() + this.timeZoneService.currentTimeZone()).toString().padStart(2, '0');
        const minutes = new Date(date).getUTCMinutes().toString().padStart(2, '0');
        return `${Number(hours) < 24 ? hours : Number(hours) - 24}:${minutes}`;
    }

}
